import React, { useEffect, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { PortalSVGIcon } from '@custom-components';
import { withTheme } from 'styled-components';
import { themePropType } from '@themes';
import {
  SideNavContainer,
  SideNavContents,
  SecuredNavLink,
  HelpSupport,
  HighlightedNav,
  RouteRect
} from './SideNav.styles';

function SideNav(props) {
  const { routeName, theme, helpSupport, sideExpanded } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  // When the loading or sideExpanded props change, reset the component state
  useEffect(() => {
    if (!isLoading && sideExpanded !== isExpanded) {
      setIsLoading(false);
      setIsExpanded(sideExpanded);
    }
  }, [sideExpanded, isLoading]);

  useLayoutEffect(() => {
    // If the DOM isn't available, do not mount the CSSTransition component
    setIsLoading(!window);
  }, []);

  if (isLoading) return null;

  const lightTheme = theme ? theme.lightTheme : 'white';
  const lightText = theme ? theme.lightText : 'white';

  const renderItems = () => {
    const { menuItems } = props;

    return menuItems.map((link) => {
      const { id, onClick, href, target, className, label, icon, routeMatches, reqPermission } = link;
      const isCurrentRoute = routeMatches.includes(routeName);

      return (
        <CSSTransition key={id} in={isExpanded} timeout={400} classNames="stretch">
          <SecuredNavLink
            href={href || '#'}
            onClick={onClick}
            target={target || null}
            className={className || null}
            title={label}
            reqPermission={reqPermission}
          >
            {isCurrentRoute && <RouteRect />}
            <HighlightedNav className={isCurrentRoute ? 'curRoute' : ''}>
              {icon && (
                <PortalSVGIcon
                  selectedIcon={icon}
                  width="24px"
                  height="24px"
                  iconColor={isCurrentRoute ? lightTheme : lightText}
                />
              )}
              <div className="label">{label}</div>
            </HighlightedNav>
          </SecuredNavLink>
        </CSSTransition>
      );
    });
  };

  return (
    <CSSTransition in={isExpanded} timeout={400} classNames="stretch">
      <SideNavContainer expanded={isExpanded}>
        <SideNavContents>
          {renderItems()}
          {helpSupport && <HelpSupport />}
        </SideNavContents>
      </SideNavContainer>
    </CSSTransition>
  );
}

export const linkPropType = PropTypes.shape({
  id: PropTypes.string,
  href: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  routeMatches: PropTypes.arrayOf(PropTypes.string)
});
export const subLinkPropType = PropTypes.shape({
  id: PropTypes.string,
  href: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  routeMatches: PropTypes.arrayOf(PropTypes.string),
  subMenu: PropTypes.arrayOf(linkPropType)
});
SideNav.propTypes = {
  sideExpanded: PropTypes.bool,
  menuItems: PropTypes.arrayOf(subLinkPropType),
  routeName: PropTypes.string,
  helpSupport: linkPropType,
  theme: themePropType
};
SideNav.defaultProps = {
  sideExpanded: false,
  menuItems: [],
  routeName: '',
  helpSupport: null,
  theme: {}
};

const ComposedSideNav = withTheme(SideNav);
export default ComposedSideNav;
